import Button from "components/Button"
import { RowContainer } from "components/Layout"
import { FilterSortPageType, GenericColumnModel, InteractionManager } from "../BaseTable"
import { Export, ExportFormFlags } from "features/Export/Export"
import { ExportDataModel, FileResponse } from "services/tenantManagementService"
import { useCallback, useMemo, useState } from "react"
import { SimpleDialog } from "components/Dialog"
import { ConfigureView } from "../"

type Props = {
	visibleColumns: GenericColumnModel[]
	hiddenColumns: GenericColumnModel[]
	filterSortPage: FilterSortPageType
	interactionManager: InteractionManager
	addHandler(): void
	deleteHandler(selectedRow: any): void
	isDeleteable?: boolean
	isCreateable?: boolean
	isViewConfigurable?: boolean
	exportFunction?(model: ExportDataModel): Promise<FileResponse>
	exportTitle?: string
	exportFormFlags?: ExportFormFlags
	renderExportComponent?: () => React.ReactElement
}

export const Toolbar = ({
	visibleColumns,
	hiddenColumns,
	filterSortPage,
	interactionManager,
	addHandler,
	deleteHandler,
	isDeleteable = true,
	isCreateable = true,
	isViewConfigurable = true,
	exportFunction,
	exportTitle,
	exportFormFlags,
	renderExportComponent
}: Props) => {
	const selectedRow = interactionManager.selectedRow; // can be undefined

	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);

	const openConfirmDeleteModal = useCallback(
		() => setIsConfirmDeleteModalOpen(true),
		[]
	);

	const closeConfirmDeleteModal = useCallback(
		() => setIsConfirmDeleteModalOpen(false),
		[]
	)

	const onDeleteConfirmed = useCallback(
		async () => {
			closeConfirmDeleteModal();
			selectedRow && deleteHandler(selectedRow);
		},
		[closeConfirmDeleteModal, deleteHandler, selectedRow]
	);

	const exportContent = useMemo(
		() => {
			if (renderExportComponent) {
				return renderExportComponent();
			}

			if (exportFunction && exportTitle) {
				return (
					<Export
						visibleColumns={visibleColumns}
						hiddenColumns={hiddenColumns}
						filterSortPage={filterSortPage}
						exportAction={exportFunction}
						title={exportTitle}
						{...exportFormFlags}
					/>
				)
			}

			return <></>
		},
		[exportFormFlags, exportFunction, exportTitle, filterSortPage, hiddenColumns, renderExportComponent, visibleColumns]
	)

	return (
		<div>
			<SimpleDialog
				open={isConfirmDeleteModalOpen}
				title='i18n.deleteModal.title'
				message='i18n.deleteModal.text'
				onConfirm={onDeleteConfirmed}
				onCancel={closeConfirmDeleteModal}
			/>
			<RowContainer justifyContent='space-between'>
				<RowContainer>
					<Button
						text='i18n.button.create'
						onClick={addHandler}
						disabled={!isCreateable}
					/>
					<Button
						text='i18n.button.delete'
						color='destructive'
						onClick={openConfirmDeleteModal}
						disabled={!selectedRow || !isDeleteable}
					/>
				</RowContainer>
				<RowContainer>
					{isViewConfigurable &&
						<ConfigureView
							visibleColumns={visibleColumns}
							hiddenColumns={hiddenColumns}
							interactionManager={interactionManager}
						/>
					}
					{exportContent}
				</RowContainer>
			</RowContainer>
		</div>
	)
}
